import { useCallback, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useEmployeeSearchCache } from './useEmployeeSearchCache';
import {
  filterMentionCandidates,
  MENTION_MIN_QUERY_LENGTH,
  MentionCandidate,
  useFeedMentionScope,
} from './useFeedMentionScope';

const DEBOUNCE_MS = 300;

/**
 * Debounced, brand-scoped employee search for @-mention candidate lists.
 * Scoped to the globally selected brand (useSelectedBrandKey), or for a common
 * feed to every brand the feed is shown in (see useFeedMentionScope).
 * Longer queries are narrowed from earlier results where possible (see useEmployeeSearchCache).
 *
 * When `socialFeedId` points at a feed limited to certain users, the search API is
 * skipped and only those users are offered.
 */
export function useMentionCandidates(socialFeedId?: string) {
  const scope = useFeedMentionScope(socialFeedId);
  const { fetchEmployees, fromCache, isFetching } = useEmployeeSearchCache();
  const [candidates, setCandidates] = useState<MentionCandidate[]>([]);
  const [isResolving, setIsResolving] = useState(false);
  // Guards against a slow response for an old query overwriting a newer one
  const latestRequest = useRef(0);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string, brandKeys: string[], request: number) => {
        fetchEmployees(query, brandKeys).then(
          (results) => {
            if (request === latestRequest.current) {
              setCandidates(results);
            }
          },
          () => {
            if (request === latestRequest.current) {
              setCandidates([]);
            }
          }
        );
      }, DEBOUNCE_MS),
    [fetchEmployees]
  );

  const search = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      const request = ++latestRequest.current;
      debouncedSearch.cancel();
      setIsResolving(false);
      if (trimmed.length < MENTION_MIN_QUERY_LENGTH || scope.status === 'pending') {
        setCandidates([]);
        return;
      }
      if (scope.status === 'restricted') {
        setIsResolving(true);
        scope
          .loadAllowedUsers()
          .then((allowed) => {
            if (request === latestRequest.current) {
              setCandidates(filterMentionCandidates(allowed, trimmed));
            }
          })
          .finally(() => {
            if (request === latestRequest.current) {
              setIsResolving(false);
            }
          });
        return;
      }
      const { brandKeys } = scope;
      const cached = fromCache(trimmed, brandKeys);
      if (!cached) {
        debouncedSearch(trimmed, brandKeys, request);
        return;
      }
      setIsResolving(true);
      cached
        .then((results) => {
          if (request !== latestRequest.current) {
            return;
          }
          if (results) {
            setCandidates(results);
          } else {
            debouncedSearch(trimmed, brandKeys, request);
          }
        })
        .finally(() => {
          if (request === latestRequest.current) {
            setIsResolving(false);
          }
        });
    },
    [debouncedSearch, fromCache, scope]
  );

  const reset = useCallback(() => {
    latestRequest.current++;
    debouncedSearch.cancel();
    setIsResolving(false);
    setCandidates([]);
  }, [debouncedSearch]);

  return {
    search,
    candidates,
    isSearching: isFetching || isResolving,
    reset,
  };
}
